import later from '@breejs/later';

import { getCurrentUser } from 'services/store-service';
import { getFormattedDuration } from 'utils/date';

later.date.localTime();

export const CRON_PERIODS = {
    '*/2': 'Every two months',
    '*/3': 'Quarterly',
    '*/4': 'Every four months',
    '*/6': 'Biannually'
};

export const CRON_MONTHS = {
    '1': 'January',
    '2': 'February',
    '3': 'March',
    '4': 'April',
    '5': 'May',
    '6': 'June',
    '7': 'July',
    '8': 'August',
    '9': 'September',
    '10': 'October',
    '11': 'November',
    '12': 'December',
};

const getLocalHour = (hour, cronTZOffset) => {
    const localHour = hour + ((cronTZOffset || 0) - (getCurrentUser().tz_offset || 0)) / 60;

    return localHour;
}

export const getFriendlyDayName = (day = '0', short) => {
    let dayName;
    switch (day.toString()) {
        case '0':
            dayName = 'Sunday';
            break;
        case '1':
            dayName = 'Monday';
            break;
        case '2':
            dayName = 'Tuesday';
            break;
        case '3':
            dayName = 'Wednesday';
            break;
        case '4':
            dayName = 'Thursday';
            break;
        case '5':
            dayName = 'Friday';
            break;
        case '6':
            dayName = 'Saturday';
            break;

        default:
            // es lint
    }

    if (short) {
        return dayName[0];
    }

    return dayName;
}

const getCronType = ({ cronDayOfMonth, cronMonth, cronDayOfWeek, biWeekly }) => {
    if (cronMonth || biWeekly) {
        if (cronMonth.indexOf('*/') >= 0 || biWeekly) {
            return 'period';
        }

        return 'year';
    }

    if (cronDayOfMonth) {
        return 'month';
    }

    if (cronDayOfWeek.length !== 1) {
        return 'day';
    }

    return 'week'
}

export const decodeCronString = ({ cronString = '', biWeekly, cronTZOffset }) => {
    if (!cronString) {
        return {
            cronType: null,
            cronStartAt: '09:00',
            cronDayOfMonth: 1,
            cronMonth: null,
            cronBiWeekly: biWeekly || 0,
            cronDayOfWeek: []
        };
    }

    const [ startAtMinute, startAtHour, dayOfMonth, month, dayOfWeek ] = cronString.split(' ');

    let showTimezone = false;
    let cronDayOfWeek = dayOfWeek === '*' ? [] : (dayOfWeek || '').split(',');
    const cronDayOfMonth = dayOfMonth === '*' ? '' : dayOfMonth;
    const cronMonth = month === '*' ? '' : month
    let localStartAtHour = getLocalHour(startAtHour - 0, cronTZOffset);

    if (localStartAtHour >= 24 || localStartAtHour < 0) {
        showTimezone = true;
        localStartAtHour = startAtHour - 0;
    }

    return {
        cronType: getCronType({ cronDayOfMonth, cronMonth, cronDayOfWeek, biWeekly }),
        cronStartAt: `${localStartAtHour.toString().padStart(2, '0')}:${startAtMinute.padStart(2, '0')}`,
        cronDayOfMonth,
        cronMonth,
        cronDayOfWeek,
        cronBiWeekly: biWeekly || 0,
        showTimezone
    };
}

export const parseSchedule = (schedule, tzOffset) => {
    const getTime = () => {
        if (schedule) {
            const offsetInSeconds = tzOffset * 60;

            if (schedule.t) {
                return schedule.t[0] + offsetInSeconds;
            }

            if (schedule.h) {
                return (schedule.h[0] * 60 * 60) + (schedule.m?.[0] || 0) * 60 + offsetInSeconds;
            }

            if (schedule.m) {
                return schedule.m[0] * 60 + offsetInSeconds;
            }
        }

        return 32400;
    }

    const time = getTime();

    if (schedule?.Y) {
        return {
            type: 'year',
            every: schedule.Y[1] - schedule.Y[0],
            month: schedule.M[0],
            dayOfMonth: schedule.D[0],
            time
        };
    }

    if (schedule?.M) {
        if (schedule.D) {
            return {
                type: 'month',
                every: schedule.M[1] - schedule.M[0],
                startingOn: schedule.M[0],
                dayOfMonth: schedule.D[0],
                time
            };
        } else {
            return {
                type: 'month',
                every: schedule.M[1] - schedule.M[0],
                weekOfMonth: schedule.wm?.[0],
                startingOn: schedule.M[0],
                dayOfWeek: schedule.d,
                time
            };
        }
    }

    if (schedule?.wm) {
        return {
            type: 'weekOfMonth',
            every: schedule.wm[1] - schedule.wm[0],
            dayOfWeek: schedule.d,
            time
        };
    }

    if (schedule?.wy) {
        return {
            type: 'weekOfYear',
            every: schedule.wy[1] - schedule.wy[0],
            dayOfWeek: schedule.d,
            time
        };
    }

    if (schedule?.dy) {
        return {
            type: 'dayOfYear',
            every: schedule.dy[1] - schedule.dy[0],
            dayOfWeek: schedule?.d,
            time
        };
    }

    if (schedule?.D) {
        return {
            type: 'dayOfMonth',
            every: schedule.D[1] - schedule.D[0],
            dayOfWeek: schedule?.d,
            time
        };
    }

    return {
        type: 'dayOfYear',
        every: 1,
        dayOfWeek: [2],
        time
    };
}

const fixScheduleWm = schedule => {
    const toFix = JSON.stringify([1, 2, 3, 4, 5]);
    if (JSON.stringify(schedule?.wm || []) === toFix) {
        schedule.wm = [1, 2, 3, 4, 5, 6];
    }
}

export const createSchedule = recur => {
    const recurObject = later.parse.recur()
        .every(recur.every)[recur.type]();

    if (typeof recur.startingOn !== 'undefined') {
        recurObject.startingOn(recur.startingOn);
    }

    if (typeof recur.dayOfWeek !== 'undefined') {
        recurObject.on(...recur.dayOfWeek).dayOfWeek();
    }

    if (typeof recur.weekOfMonth !== 'undefined') {
        recurObject.on(recur.weekOfMonth).weekOfMonth();
    }

    if (typeof recur.weekOfYear !== 'undefined') {
        recurObject.on(recur.weekOfYear).weekOfYear();
    }

    if (typeof recur.month !== 'undefined') {
        recurObject.on(recur.month).month();
    }

    if (typeof recur.dayOfMonth !== 'undefined') {
        recurObject.on(recur.dayOfMonth).dayOfMonth();
    }

    if (typeof recur.dayOfYear !== 'undefined') {
        recurObject.on(recur.dayOfYear).dayOfYear();
    }

    recurObject.on(getFormattedDuration(1000 * recur.time)).time();

    fixScheduleWm(recurObject?.schedules?.[0]);

    return recurObject;
}
